import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable, Injector } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { COMPONENT_LOADER_REF, LoaderComponent } from './loader/loader.component';


export type LoaderState = 'loadingAfterTime' | 'loading' | 'loaded' | 'error';

@Injectable({
    providedIn: 'root',
})
export class LoaderService {
    documentLoader$ = new Subject<LoaderState>();
    private state: LoaderState = 'loaded';
    private loaderFromNavigation = false;
    error: string | null = null;
    cancel$ = new BehaviorSubject<true | null>(null);

    overlayRef = this.overlay.create({
        positionStrategy: this.overlay.position()
                                      .global()
                                      .centerHorizontally()
                                      .centerVertically(),
        hasBackdrop: true
    });
    componentRef: ComponentRef<LoaderComponent> = null;

    timeoutId: number = null;
    timeout = 15000;

    loadedDocument: string = null;

    noLoadedDocumentExists: boolean = null;

    constructor( private router: Router, private overlay: Overlay) {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.setLoadingAfterTime(true);
                this.updateLoadedDocument(event.url);
            }
            if (event instanceof NavigationEnd) {
                this.setLoaded(true);
            }
            if (event instanceof NavigationCancel) {
                this.setLoaded(true);
            }
        });
    }

    updateLoadedDocument(url: string) {
        const match = url.match(/\/offer\/([a-zA-Z0-9-]+)/);
        if (match) {
            this.loadedDocument = match[1];
        }
    }

    setLoadingAfterTime(fromNavigation = false, time = 1000) {
        this.loaderFromNavigation = fromNavigation;
        if (this.state !== 'loadingAfterTime') {
            this.state = 'loadingAfterTime';
            setTimeout(
                () => {
                if (this.state === 'loadingAfterTime') {
                    this.setLoading();
                }
            }, time);
        }
    }

    setLoading() {
        this.state = 'loading';
        this.documentLoader$.next('loading');
        if (this.overlayRef.hasAttached()) {
            return;
        }
        const injector = Injector.create({
            providers: [{ provide: COMPONENT_LOADER_REF, useValue: {
                cancel: this.cancel.bind(this),
            } }],
          });
        this.componentRef = this.overlayRef.attach(new ComponentPortal(LoaderComponent, undefined, injector));
        this.timeoutId = window.setTimeout(() => {
            if (this.error === null) {
                this.setError('TIMEOUT');
            }
        }, this.timeout)
    }

    setLoaded(fromNavigation = false) {
        if (!fromNavigation || (fromNavigation && this.loaderFromNavigation)) {
            clearTimeout(this.timeoutId);
            this.state = 'loaded';
            this.documentLoader$.next('loaded');
            this.overlayRef.detach();
            this.componentRef = null;
            this.error = null;
            this.cancel$.next(null);
            this.loadedDocument = null;
            this.noLoadedDocumentExists = null;
        }
    }

    setError(error = null, docIds?: string[]) {
        if (this.state === 'loading' && (!docIds || docIds.includes(this.loadedDocument)) ) {
            this.state = 'error';
            this.documentLoader$.next('error');
            this.error = error;
            if (error === 'NO_ACCESS') {
                this.componentRef.instance.setError(error, 2);
            } else if (error === 'SYNC_ERROR') {
                this.componentRef.instance.setError(error, 30);
            } else {
                this.componentRef.instance.setError(error);
            }
            clearTimeout(this.timeoutId);
        }
    }

    cancel() {
        this.cancel$.next(true);
    }

    afterNoDocumentLoaded() {
        this.noLoadedDocumentExists = true;
        if (this.componentRef?.instance) {
            this.componentRef.instance.showGeneralError();
        }
    }
}
